import { Button, InputAdornment, Typography } from '@mui/material'
import React from 'react'
import { z } from 'zod'

import DeleteDialog from '@/app/component/atom/delete-dialog'
import { Form } from '@/app/component/form'
import { ActionsContainer } from '@/app/component/form/common'
import SubmitButton from '@/app/component/form/submit-button'
import type { Option } from '@/app/component/form/types'
import { MAX_AMOUNT, MIN_AMOUNT } from '@/app/module/payments/definitions'
import { useChargeCreditCard } from '@/app/module/payments/hooks/use-charge-credit-card'
import { useDeleteCreditCard } from '@/app/module/payments/hooks/use-delete-credit-card'

type Props = {
  amount?: number
  cards: Option[]
}

const AddFundForm: React.FC<Props> = ({ amount, cards }) => {
  const [confirmOpen, setConfirmOpen] = React.useState(false)
  const [deleteOpen, setDeleteOpen] = React.useState(false)

  const [chargeCard, { isLoading: charging }] = useChargeCreditCard()
  const [deleteCard, { isLoading: deleting }] = useDeleteCreditCard()

  const handleSubmit = React.useCallback(() => {
    setConfirmOpen(true)
  }, [])

  const handleConfirm = React.useCallback(
    async (values: z.infer<typeof schema>) => {
      setConfirmOpen(false)
      await chargeCard({ ccid: values.cardId, amountUSD: Number(values.amount) })
    },
    [chargeCard],
  )

  const defaultValues = React.useMemo(
    () => ({
      amount: amount || MIN_AMOUNT,
      cardId: cards[0]?.value || '',
    }),
    [amount, cards],
  )

  const fields = React.useMemo(
    () =>
      ({
        amount: {
          fieldProps: {
            autoFocus: true,
            label: 'Amount',
            name: 'amount',
            required: true,
            min: MIN_AMOUNT,
            max: MAX_AMOUNT,
            startAdornment: (
              <InputAdornment position="start" sx={{ pb: '4px' }}>
                $
              </InputAdornment>
            ),
            type: 'number',
          },
          type: 'text',
        },
        cardId: {
          fieldProps: {
            label: 'Saved cards',
            name: 'cardId',
            options: cards,
          },
          type: 'radio',
        },
      }) as const,
    [cards],
  )

  return (
    <Form
      defaultValues={defaultValues}
      fields={fields}
      name="add-fund"
      schema={schema}
      shouldReset
      onSubmit={handleSubmit}
      renderActions={(_, methods) => (
        <ActionsContainer
          sx={{
            justifyContent: 'space-between',
            alignContent: 'center',
          }}
        >
          <Typography color="textSecondary" variant="caption">
            Minimum amount is ${MIN_AMOUNT} and Maximum amount is ${MAX_AMOUNT}
          </Typography>
          <div style={{ display: 'flex', gap: 8 }}>
            <Button
              color="primary"
              disabled={!methods?.getValues('cardId') || cards.length === 0}
              loading={deleting}
              variant="outlined"
              onClick={() => setDeleteOpen(true)}
            >
              Delete card
            </Button>
            <SubmitButton isDirty isSubmitting={charging}>
              Pay
            </SubmitButton>
          </div>
          <DeleteDialog
            cancelButtonText="Cancel"
            deleteButtonText="Confirm"
            icon="credit_card"
            isOpen={confirmOpen}
            text={methods ? `Are you sure you want to charge your card with $${methods.getValues('amount')}?` : ''}
            title="Confirm charge"
            onClose={() => setConfirmOpen(false)}
            onConfirm={() => handleConfirm(methods.getValues())}
          />
          <DeleteDialog
            cancelButtonText="Cancel"
            deleteButtonText="Delete"
            disabled={deleting}
            icon="delete"
            isOpen={deleteOpen}
            text={
              methods
                ? `Are you sure you want to delete the selected card${(() => {
                    const selected = methods.getValues('cardId')
                    const label = cards.find((c) => c.value === selected)?.label
                    return label ? ` (${label})` : ''
                  })()}?`
                : ''
            }
            title="Delete saved card"
            onClose={() => setDeleteOpen(false)}
            onConfirm={async () => {
              const id = methods?.getValues('cardId')
              setDeleteOpen(false)
              if (id) {
                await deleteCard(id)
              }
            }}
          />
        </ActionsContainer>
      )}
    />
  )
}

const schema = z.object({
  amount: z
    .number({ invalid_type_error: 'Enter a valid amount' })
    .min(MIN_AMOUNT, `Amount is less than minimum amount of $${MIN_AMOUNT}`)
    .max(MAX_AMOUNT, `Amount is more than maximum amount of $${MAX_AMOUNT}`),
  cardId: z.string().min(1, 'Select a card'),
})

export default AddFundForm
